import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
} from '@mui/material';
import SolarStructureScene from '../components/designPlant/SolarStructureScene';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import StructureDetails from '../components/designPlant/StructureDetails';
import { useLocation } from 'react-router-dom';

export default function DesignPlant() {
  const [tileAngleDeg, setTileAngleDeg] = useState(18);
  const [lowestLegHeight, setLowestLegHeight] = useState(2.5);
  const [tableCols, setTableCols] = useState(3);
  const [tableRows, setTableRows] = useState(2);
  const [structureData, setStructureData] = useState(null);
  const [humanZPosition, setHumanZPosition] = useState(2);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogPinCode, setDialogPinCode] = useState('');

  const [dialogError, setDialogError] = useState('');
  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);

  const cancelAlert = () => {
    setShowAlert(false);
  };

  useEffect(() => {
    if (openDialog) {
      setShowAlert(true);
    }
  }, [openDialog]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const initialFormData = {
    pin_code: queryParams.get('pin_code'),
    system_capacity_in_kw: parseFloat(queryParams.get('system_capacity_in_kw')),
    tilt: parseFloat(queryParams.get('tilt')),
  };
  const [formData, setFormData] = useState(initialFormData);

  const handleGetSolarGeneration = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogPinCode('');
    setDialogError('');
  };

  const handleDialogSubmit = () => {
    const newErrors = {};

    if (!dialogPinCode || !/^\d{6}$/.test(dialogPinCode)) {
      setDialogError('Please enter a valid 6-digit PIN code');
      return;
    }

    if (
      !formData.system_capacity_in_kw ||
      formData.system_capacity_in_kw <= 0
    ) {
      newErrors.system_capacity_in_kw = 'Please enter a valid plant size';
      setErrors(newErrors);
      return;
    }

    const params = new URLSearchParams({
      pin_code: dialogPinCode,
      system_capacity_in_kw: formData.system_capacity_in_kw,
      tilt: '12.0',
    });
    window.open(`/solar-generation?${params.toString()}`, '_blank');
    handleDialogClose();
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 2 }}>
      <Typography
        textAlign={isMobile ? 'center' : 'left'}
        variant={isMobile ? 'h4' : 'h3'}
        sx={{
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: 700,
          letterSpacing: '-0.5px',
          WebkitTextStroke: isMobile ? '0.6px black' : '1.4px black',
          color: '#000',
        }}
        gutterBottom
      >
        Solar design preview
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
          }}
        >
          <Stack sx={{ width: '80%' }}>
            <Box
              sx={{
                flex: 3,
                borderRadius: '9px',
                width: isMobile ? '100%' : '100%',
                height: isMobile ? '80vh' : '100%',
              }}
            >
              <SolarStructureScene
                tileAngleDeg={tileAngleDeg}
                lowestLegHeight={lowestLegHeight}
                tableCols={tableCols}
                tableRows={tableRows}
                setStructureData={setStructureData}
                humanZPosition={humanZPosition}
              />
            </Box>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: '9px',
                border: '1px solid lightgray',
              }}
            >
              <Stack
                mb={3}
                flexDirection='row'
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 600, color: '#2D3E50' }}
                >
                  Configuration
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label='Tilt (°)'
                    type='number'
                    inputProps={{ min: 0, max: 30 }}
                    value={tileAngleDeg}
                    onChange={(e) => setTileAngleDeg(Number(e.target.value))}
                    size='small'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label='Lowest leg height (m)'
                    type='number'
                    inputProps={{ min: 0, step: 0.1 }}
                    value={lowestLegHeight}
                    onChange={(e) => setLowestLegHeight(Number(e.target.value))}
                    size='small'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label='Panels in 1 row'
                    type='number'
                    inputProps={{ min: 1 }}
                    value={tableCols}
                    onChange={(e) => setTableCols(Number(e.target.value))}
                    size='small'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label='Panels in 1 column'
                    type='number'
                    inputProps={{ min: 1 }}
                    value={tableRows}
                    onChange={(e) => setTableRows(Number(e.target.value))}
                    size='small'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label='Man Z Position (m)'
                    type='number'
                    inputProps={{ min: -10, max: 10, step: 0.5 }}
                    value={humanZPosition}
                    onChange={(e) => setHumanZPosition(Number(e.target.value))}
                    size='small'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}></Grid>
                <Grid item xs={12} sm={6} md={3}></Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  display='flex'
                  justifyContent='flex-end'
                >
                  <Button
                    variant='contained'
                    onClick={handleGetSolarGeneration}
                    sx={{
                      borderRadius: '32px',
                      backgroundColor: '#A8D5A8',
                      color: '#000',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#91c291',
                      },
                    }}
                  >
                    Get solar generation
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
          <Dialog
            open={openDialog}
            onClose={handleDialogClose}
            maxWidth='sm'
            fullWidth
          >
            {/* Alert: appears for 800ms, placed above title to avoid layout shift */}
            {showAlert && (
              <Alert severity='info' sx={{ m: 2, mt: 3 }} onClose={cancelAlert}>
                <Typography variant='body2'>
                  Please enter your 6-digit PIN code and the plant size (kW) to
                  get the solar generation data.
                </Typography>
              </Alert>
            )}

            <DialogTitle>Enter PIN code</DialogTitle>

            <DialogContent>
              <DialogContentText>
                <Stack direction='row' spacing={1} alignItems='center' mt={1}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{ mt: 1, mb: 2 }}
                  >
                    Tilt value =
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 2, color: '#000' }}>
                    {tileAngleDeg}°
                  </Typography>
                </Stack>

                <Stack direction='row' spacing={1} alignItems='center' mb={2}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{ mt: 1, mb: 2 }}
                  >
                    Panel count =
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 2, color: '#000' }}>
                    {tableCols * tableRows} panels
                  </Typography>
                </Stack>
              </DialogContentText>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label='PIN Code'
                    type='text'
                    size='small'
                    variant='outlined'
                    fullWidth
                    value={dialogPinCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,6}$/.test(value)) {
                        setDialogPinCode(value);
                      }
                    }}
                    error={!!dialogError}
                    helperText={dialogError}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 6,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label='Plant size (kW)'
                    type='number'
                    size='small'
                    fullWidth
                    variant='outlined'
                    value={formData.system_capacity_in_kw}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                        handleInputChange('system_capacity_in_kw')({
                          target: { value },
                        });
                      }
                    }}
                    error={!!errors.system_capacity_in_kw}
                    helperText={errors.system_capacity_in_kw}
                    inputProps={{
                      step: '0.01',
                      min: 0,
                      max: 999999,
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button
                onClick={handleDialogSubmit}
                variant='contained'
                endIcon={<ArrowCircleRightIcon />}
                sx={{
                  borderRadius: '32px',
                  backgroundColor: '#A8D5A8',
                  color: '#000',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#91c291',
                  },
                }}
              >
                Get
              </Button>
            </DialogActions>
          </Dialog>
          <StructureDetails data={structureData} />
        </Box>
      </Box>
    </Box>
  );
}
